import React, { useState } from 'react'
import { GetStaticProps, NextPage } from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { Layout } from '@components/layout'
import { HeaderSection } from '@components/header-section'
import SEO from '@components/shared/seo'
import { Pagination } from '@components/shared/pagination'

import { MenusApi, Menu } from '@services/api/menus'
import { ArticlesApi } from '@services/api/articles'
import { Article } from '@helpers/article-response'
import { AdsApi } from '@services/api/ads'
import { AdsIndexDto } from '@services/api/ads/types'

interface Specialist {
  id: number
  name: string
  photo: string
  bio: string
  expertise: string[]
  articleCount: number
}

interface AllSpecialistsPageProps {
  main: Array<Menu>
  footer1: Array<Menu>
  footer2: Array<Menu>
  specialists: Array<Specialist>
  ads: AdsIndexDto[]
  recentArticles?: Article[]
}

const AllSpecialistsPage: NextPage<AllSpecialistsPageProps> = ({
  main,
  footer1,
  footer2,
  specialists,
  ads,
  recentArticles = []
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  // Filtrar especialistas baseado no termo de busca e categoria
  const filteredSpecialists = specialists.filter(specialist => {
    const matchesSearch =
      specialist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      specialist.bio.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory =
      selectedCategory === 'all' ||
      specialist.expertise.some(skill =>
        skill.toLowerCase().includes(selectedCategory.toLowerCase())
      )
    return matchesSearch && matchesCategory
  })

  const categories = [
    'all',
    'jurídico',
    'técnico',
    'financeiro',
    'comercial',
    'regulamentação'
  ]

  return (
    <Layout
      mainMenus={main}
      footerMenus1={footer1}
      footerMenus2={footer2}
      title="Todos os Especialistas - Portal Solar"
      logoHeading={true}
      logoUrl="/especialistas"
      ad={ads?.find(ad => ad.code === 'ad_sponsor_main_top')}
    >
      <Head>
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Todos os Especialistas - Portal Solar"
        />
        <meta
          property="og:description"
          content="Conheça todos os especialistas em energia solar do Portal Solar. Encontre o especialista ideal para suas necessidades."
        />
      </Head>

      <SEO
        title="Todos os Especialistas - Portal Solar"
        description="Conheça todos os especialistas em energia solar do Portal Solar. Encontre o especialista ideal para suas necessidades."
        canonicalUrl={`${
          process.env.NEXT_PUBLIC_URL_BASE || 'https://www.portalsolar.com.br'
        }/especialistas/todos`}
        type="website"
        shouldIndexPage={true}
      />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-4">
        <nav className="text-sm text-gray-500">
          <Link href="/" className="hover:text-gray-700">
            Home
          </Link>
          {' > '}
          <Link href="/especialistas" className="hover:text-gray-700">
            Especialistas
          </Link>
          {' > '}
          <span className="text-gray-900">Todos os especialistas</span>
        </nav>
      </div>

      {/* Header */}
      <HeaderSection className="py-8">
        <h1 className="text-4xl font-bold mb-4">Todos os Especialistas</h1>
        <p className="text-xl text-gray-600">
          Conheça nossos {specialists.length} especialistas em energia solar
        </p>
      </HeaderSection>

      {/* Filtros e busca */}
      <section className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Barra de busca */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar especialistas..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Filtro por categoria */}
          <div className="flex gap-2 flex-wrap">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category === 'all'
                  ? 'Todos'
                  : category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Resultados */}
        <div className="mb-6">
          <p className="text-gray-600">
            Mostrando {filteredSpecialists.length} de {specialists.length}{' '}
            especialistas
          </p>
        </div>

        {/* Grid de especialistas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredSpecialists.map(specialist => (
            <Link key={specialist.id} href={`/especialistas/${specialist.id}`}>
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
                <div className="relative h-48">
                  <Image
                    src={specialist.photo || '/assets/default-specialist.jpg'}
                    alt={specialist.name}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-4 flex flex-col h-48">
                  <h3 className="font-bold text-lg mb-2">{specialist.name}</h3>
                  <p className="text-sm text-gray-600 mb-3 flex-grow">
                    {specialist.bio}
                  </p>

                  {/* Expertise tags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {specialist.expertise.slice(0, 3).map((skill, index) => (
                      <span
                        key={index}
                        className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs"
                      >
                        {skill}
                      </span>
                    ))}
                    {specialist.expertise.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{specialist.expertise.length - 3} mais
                      </span>
                    )}
                  </div>

                  {/* Estatísticas */}
                  <div className="text-xs text-gray-500">
                    {specialist.articleCount} artigos publicados
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mensagem quando não há resultados */}
        {filteredSpecialists.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">
              Nenhum especialista encontrado com os critérios selecionados.
            </p>
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory('all')
              }}
              className="text-orange-500 hover:text-orange-600 font-medium"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {/* Paginação (se necessário) */}
        {filteredSpecialists.length > 20 && (
          <div className="mt-8">
            <Pagination
              currentPage={1}
              pageSize={20}
              total={filteredSpecialists.length}
            />
          </div>
        )}
      </section>

      {/* Call to action */}
      <section className="container mx-auto px-4 py-8">
        <div className="bg-orange-50 rounded-lg p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Quer se tornar um especialista?
          </h2>
          <p className="text-gray-600 mb-6">
            Junte-se à nossa comunidade de especialistas e compartilhe seu
            conhecimento
          </p>
          <button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
            Candidatar-se
          </button>
        </div>
      </section>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    const [menus, recentArticlesResponse] = await Promise.all([
      MenusApi.all(),
      ArticlesApi.index({ per: 12 })
    ])

    const {
      data: { ads }
    } = await AdsApi.index({
      codes: ['ad_sponsor_main_top', 'ad_specialists_sidebar']
    })

    // Mock data para especialistas - isso deve vir da API depois
    const specialists: Specialist[] = Array.from(
      { length: 24 },
      (_, index) => ({
        id: index + 1,
        name: `Especialista ${index + 1}`,
        photo: `/assets/specialists/specialist-${(index % 4) + 1}.jpg`,
        bio: `Especialista em energia solar com experiência em ${
          ['instalação', 'manutenção', 'vendas', 'projeto'][index % 4]
        }`,
        expertise: [
          ['Técnico', 'Instalação'],
          ['Comercial', 'Vendas'],
          ['Jurídico', 'Regulamentação'],
          ['Financeiro', 'Investimentos']
        ][index % 4],
        articleCount: Math.floor(Math.random() * 50) + 1
      })
    )

    return {
      props: {
        main: menus.main,
        footer1: menus.footer1,
        footer2: menus.footer2,
        specialists,
        ads: ads || [],
        recentArticles: recentArticlesResponse?.data?.articles || []
      },
      revalidate: 60 * 30 // 30 minutes
    }
  } catch (error) {
    console.error('Error fetching specialists data:', error)
    return {
      props: {
        main: [],
        footer1: [],
        footer2: [],
        specialists: [],
        ads: [],
        recentArticles: []
      }
    }
  }
}

export default AllSpecialistsPage
